// internal/PersonalityStudioPage.jsx — Studio Fiches AYA ID (owner uniquement).
// Saisie de quelques informations → génération IA → brouillon → prévisualisation → publication manuelle.
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Eye, Globe, GlobeLock, Trash2, UserRound, Loader2, Camera, ImagePlus, X } from "lucide-react";
import InternalLayout, { S, IT } from "@/internal/InternalLayout";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api/internal`;
const PUB_API = `${process.env.REACT_APP_BACKEND_URL || ""}/api/personalities`;
const hdrs = () => ({ "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("civ_user_token") || ""}` });
const EMPTY = { name: "", short_name: "", role_type: "professional", title: "", org: "", location: "", quote: "", facts: "", portrait: "" };

// Redimensionne un fichier image en dataURL (max 1280px) avant upload.
function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const max = 1280;
      const ratio = Math.min(1, max / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * ratio);
      canvas.height = Math.round(img.height * ratio);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL("image/jpeg", 0.85));
    };
    img.onerror = reject;
    img.src = url;
  });
}

async function uploadPhoto(file) {
  const image = await fileToDataUrl(file);
  const r = await fetch(`${API}/personalities-studio/upload-photo`, { method: "POST", headers: hdrs(), body: JSON.stringify({ image }) });
  const j = await r.json();
  if (!j.ok) throw new Error(j.error || "upload");
  return j.url;
}

const F = ({ label, children }) => (
  <label style={{ display: "block", marginBottom: 10 }}>
    <span style={{ display: "block", fontSize: 11, fontWeight: 800, color: IT.muted, marginBottom: 4 }}>{label}</span>
    {children}
  </label>
);
const inp = { width: "100%", boxSizing: "border-box", background: "#0A0F0C", border: "1px solid rgba(46,226,123,0.2)", borderRadius: 10, padding: "9px 11px", color: IT.text, fontSize: 12.5, outline: "none" };

export default function PersonalityStudioPage() {
  const [list, setList] = useState(null);
  const [form, setForm] = useState({ ...EMPTY });
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [busy, setBusy] = useState(null);
  const portraitRef = useRef(null);
  const galleryRef = useRef(null);
  const rowRef = useRef(null);
  const rowSlugRef = useRef(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const load = () => fetch(`${API}/personalities-studio`, { headers: hdrs() }).then((r) => r.json()).then((j) => setList(j.ok ? j.personalities : [])).catch(() => setList([]));
  useEffect(() => { load(); }, []);

  const pickPortrait = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try { const url = await uploadPhoto(file); setForm((f) => ({ ...f, portrait: url })); toast.success("Portrait importé."); }
    catch { toast.error("Import du portrait impossible (jpeg/png/webp, 5 Mo max)."); }
    finally { setUploading(false); }
  };

  const pickGallery = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 10);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) urls.push(await uploadPhoto(f));
      setPhotos((p) => [...p, ...urls].slice(0, 10));
      toast.success(`${urls.length} photo(s) ajoutée(s) à la galerie.`);
    } catch { toast.error("Import de photos interrompu (jpeg/png/webp, 5 Mo max)."); }
    finally { setUploading(false); }
  };

  // Ajout de photos sur une fiche EXISTANTE (bouton par ligne)
  const pickRowPhotos = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 10);
    const slug = rowSlugRef.current;
    e.target.value = "";
    if (!files.length || !slug) return;
    setBusy(slug);
    try {
      const urls = [];
      for (const f of files) urls.push(await uploadPhoto(f));
      const cur = await fetch(`${PUB_API}/${slug}`, { headers: hdrs() }).then((r) => r.json());
      const existing = cur.ok ? cur.personality.photos || [] : [];
      const r = await fetch(`${API}/personalities-studio/${slug}`, { method: "PUT", headers: hdrs(), body: JSON.stringify({ photos: [...existing, ...urls].slice(0, 20) }) });
      const j = await r.json();
      if (!j.ok) throw new Error();
      toast.success(`${urls.length} photo(s) ajoutée(s) à la fiche.`);
    } catch { toast.error("Ajout de photos impossible."); }
    finally { setBusy(null); }
  };

  const generate = async () => {
    if (!form.name.trim() || !form.title.trim()) { toast.error("Nom et métier/titre sont obligatoires."); return; }
    setGenerating(true);
    try {
      const r = await fetch(`${API}/personalities-studio/generate`, { method: "POST", headers: hdrs(), body: JSON.stringify({ ...form, photos }) });
      const j = await r.json();
      if (!j.ok) { toast.error(j.error === "generation_failed" ? "La génération IA a échoué, réessaie." : "Création impossible."); return; }
      toast.success(`Fiche « ${j.personality.short_name} » générée en brouillon.`);
      setForm({ ...EMPTY });
      setPhotos([]);
      load();
    } catch { toast.error("Erreur réseau."); } finally { setGenerating(false); }
  };

  const action = async (slug, verb) => {
    setBusy(slug);
    try {
      const r = await fetch(`${API}/personalities-studio/${slug}${verb === "delete" ? "" : `/${verb}`}`, { method: verb === "delete" ? "DELETE" : "POST", headers: hdrs() });
      const j = await r.json();
      if (!j.ok) { toast.error(j.error === "seed_protected" ? "Les 4 fiches socles sont protégées." : "Action impossible."); return; }
      toast.success(verb === "publish" ? "Fiche publiée — visible dans AYA Search." : verb === "unpublish" ? "Fiche repassée en brouillon." : "Fiche supprimée.");
      load();
    } catch { toast.error("Erreur réseau."); } finally { setBusy(null); }
  };

  return (
    <InternalLayout title="Fiches AYA ID" subtitle="Crée une fiche personnalité en quelques informations : l'IA génère la fiche complète en brouillon, tu publies quand c'est prêt.">
      <div style={{ display: "grid", gridTemplateColumns: "minmax(300px, 420px) 1fr", gap: 16, alignItems: "start" }} data-testid="personality-studio">
        {/* ── Formulaire de création ── */}
        <div style={{ ...S.card, padding: 16 }} data-testid="ps-create-card">
          <h2 style={{ ...S.h2, display: "flex", alignItems: "center", gap: 7, marginBottom: 12 }}><Sparkles size={15} color={IT.green} /> Nouvelle fiche (génération IA)</h2>
          <F label="Nom complet *"><input style={inp} data-testid="ps-name" value={form.name} onChange={set("name")} placeholder="Ex : Didier Drogba" /></F>
          <F label="Nom court (affiché)"><input style={inp} data-testid="ps-short-name" value={form.short_name} onChange={set("short_name")} placeholder="Ex : Didier Drogba" /></F>
          <F label="Type de rendu">
            <select style={inp} data-testid="ps-role-type" value={form.role_type} onChange={set("role_type")}>
              <option value="professional">Professionnel (modèle Maître Allegra)</option>
              <option value="artist">Artiste (modèle Alpha Blondy)</option>
            </select>
          </F>
          <F label="Métier / Titre *"><input style={inp} data-testid="ps-title" value={form.title} onChange={set("title")} placeholder="Ex : Footballeur international · Entrepreneur" /></F>
          <F label="Organisation"><input style={inp} data-testid="ps-org" value={form.org} onChange={set("org")} placeholder="Ex : Fondation Didier Drogba" /></F>
          <F label="Localisation"><input style={inp} data-testid="ps-location" value={form.location} onChange={set("location")} placeholder="Ex : Abidjan, Côte d'Ivoire" /></F>
          <F label="Citation (optionnel)"><input style={inp} data-testid="ps-quote" value={form.quote} onChange={set("quote")} /></F>
          <F label="Faits clés (une ligne par fait — base de la génération)">
            <textarea style={{ ...inp, minHeight: 90, resize: "vertical" }} data-testid="ps-facts" value={form.facts} onChange={set("facts")} placeholder={"Ex :\nMeilleur buteur de l'histoire des Éléphants\nFondation active dans la santé depuis 2007"} />
          </F>
          <F label="Portrait (photo principale)">
            <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
              {form.portrait && <img src={form.portrait} alt="" style={{ width: 42, height: 42, borderRadius: 10, objectFit: "cover" }} data-testid="ps-portrait-preview" />}
              <button style={{ ...S.ghost, padding: "8px 12px" }} data-testid="ps-portrait-upload-btn" disabled={uploading} onClick={() => portraitRef.current?.click()}>
                <Camera size={13} /> {uploading ? "Import…" : form.portrait ? "Changer" : "Importer une photo"}
              </button>
              {form.portrait && <button style={{ ...S.ghost, padding: "8px 10px", color: "#F87171" }} data-testid="ps-portrait-clear" onClick={() => setForm((f) => ({ ...f, portrait: "" }))}><X size={13} /></button>}
              <input ref={portraitRef} type="file" accept="image/jpeg,image/png,image/webp" style={{ display: "none" }} data-testid="ps-portrait-input" onChange={pickPortrait} />
            </div>
            <input style={{ ...inp, marginTop: 7 }} data-testid="ps-portrait" value={form.portrait} onChange={set("portrait")} placeholder="…ou colle une URL (/aya/people/… ou https://…)" />
          </F>
          <F label={`Photos de la galerie (${photos.length}/10 — onglet Photos de la fiche)`}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
              {photos.map((u) => (
                <span key={u} style={{ position: "relative" }} data-testid="ps-gallery-thumb">
                  <img src={u} alt="" style={{ width: 52, height: 52, borderRadius: 10, objectFit: "cover" }} />
                  <button onClick={() => setPhotos((p) => p.filter((x) => x !== u))} style={{ position: "absolute", top: -6, right: -6, width: 18, height: 18, borderRadius: "50%", border: "none", background: "#F87171", color: "#fff", cursor: "pointer", display: "grid", placeItems: "center", padding: 0 }}><X size={11} /></button>
                </span>
              ))}
              <button style={{ width: 52, height: 52, borderRadius: 10, border: "1.5px dashed rgba(46,226,123,0.4)", background: "transparent", color: IT.green, cursor: "pointer", display: "grid", placeItems: "center" }} data-testid="ps-gallery-add-btn" disabled={uploading} onClick={() => galleryRef.current?.click()}>
                <ImagePlus size={17} />
              </button>
              <input ref={galleryRef} type="file" accept="image/jpeg,image/png,image/webp" multiple style={{ display: "none" }} data-testid="ps-gallery-input" onChange={pickGallery} />
            </div>
          </F>
          <button style={{ ...S.btn, width: "100%", justifyContent: "center", opacity: generating ? 0.6 : 1 }} data-testid="ps-generate-btn" disabled={generating || uploading} onClick={generate}>
            {generating ? <><Loader2 size={14} className="animate-spin" /> Génération en cours…</> : <><Sparkles size={14} /> Générer la fiche (brouillon)</>}
          </button>
          <p style={{ fontSize: 10, color: IT.muted, marginTop: 8 }}>Contenu généré marqué « éditorial AYA » — rien n'est publié sans ton clic « Publier ».</p>
        </div>

        {/* ── Liste des fiches ── */}
        <div style={{ ...S.card, padding: 16 }} data-testid="ps-list-card">
          <h2 style={{ ...S.h2, marginBottom: 12 }}>Fiches existantes</h2>
          {list === null ? <p style={{ color: IT.muted, fontSize: 12 }}>Chargement…</p> :
            list.length === 0 ? <p style={{ color: IT.muted, fontSize: 12 }}>Aucune fiche.</p> :
            list.map((p) => (
              <div key={p.slug} data-testid={`ps-item-${p.slug}`} style={{ display: "flex", alignItems: "center", gap: 11, padding: "10px 0", borderBottom: "1px solid rgba(46,226,123,0.09)" }}>
                {p.portrait ? <img src={p.portrait} alt="" style={{ width: 38, height: 38, borderRadius: "50%", objectFit: "cover" }} />
                  : <span style={{ width: 38, height: 38, borderRadius: "50%", background: "rgba(46,226,123,0.12)", display: "grid", placeItems: "center" }}><UserRound size={17} color={IT.green} /></span>}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 800, color: IT.text }}>{p.name} {p.is_seed && <span style={{ fontSize: 9, color: IT.gold, fontWeight: 900 }}>SOCLE</span>}</div>
                  <div style={{ fontSize: 10.5, color: IT.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.title}</div>
                </div>
                <span data-testid={`ps-status-${p.slug}`} style={{ fontSize: 9, fontWeight: 900, letterSpacing: 0.4, padding: "3px 9px", borderRadius: 999, background: p.status === "published" ? "rgba(46,226,123,0.14)" : "rgba(232,184,75,0.14)", color: p.status === "published" ? IT.green : IT.gold }}>
                  {p.status === "published" ? "PUBLIÉE" : "BROUILLON"}
                </span>
                <a href={`/personnalite/${p.slug}`} target="_blank" rel="noreferrer" style={{ ...S.ghost, padding: "6px 10px" }} data-testid={`ps-preview-${p.slug}`} title="Prévisualiser"><Eye size={13} /></a>
                <button style={{ ...S.ghost, padding: "6px 10px" }} data-testid={`ps-add-photos-${p.slug}`} disabled={busy === p.slug} title="Ajouter des photos" onClick={() => { rowSlugRef.current = p.slug; rowRef.current?.click(); }}><ImagePlus size={13} /></button>
                {p.status === "published" ? (
                  <button style={{ ...S.ghost, padding: "6px 10px", color: IT.gold, borderColor: "rgba(232,184,75,0.3)", background: "rgba(232,184,75,0.07)" }} data-testid={`ps-unpublish-${p.slug}`} disabled={busy === p.slug} onClick={() => action(p.slug, "unpublish")} title="Dépublier"><GlobeLock size={13} /></button>
                ) : (
                  <button style={{ ...S.btn, padding: "6px 10px" }} data-testid={`ps-publish-${p.slug}`} disabled={busy === p.slug} onClick={() => action(p.slug, "publish")} title="Publier"><Globe size={13} /> Publier</button>
                )}
                {!p.is_seed && (
                  <button style={{ ...S.ghost, padding: "6px 10px", color: "#F87171", borderColor: "rgba(248,113,113,0.3)", background: "rgba(248,113,113,0.06)" }} data-testid={`ps-delete-${p.slug}`} disabled={busy === p.slug} onClick={() => window.confirm(`Supprimer la fiche ${p.name} ?`) && action(p.slug, "delete")} title="Supprimer"><Trash2 size={13} /></button>
                )}
              </div>
            ))}
          <input ref={rowRef} type="file" accept="image/jpeg,image/png,image/webp" multiple style={{ display: "none" }} data-testid="ps-row-photos-input" onChange={pickRowPhotos} />
        </div>
      </div>
    </InternalLayout>
  );
}
